import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  BarChart3, DollarSign, TrendingUp, AlertTriangle, RotateCcw,
  Wallet, XCircle, ShieldAlert, Clock, Users2, Loader2, Calendar,
} from "lucide-react";

const money = (v) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const toISO = (dateStr, endOfDay = false) => {
  if (!dateStr) return "";
  const d = new Date(dateStr + (endOfDay ? "T23:59:59Z" : "T00:00:00Z"));
  return d.toISOString();
};

function Kpi({ icon: Icon, label, value, sub, tone = "text-zinc-900", testid }) {
  return (
    <div
      className="bg-white border border-zinc-200 rounded-xl card-crisp p-4"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-zinc-500" />
        <div className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500">{label}</div>
      </div>
      <div className={`text-2xl font-bold ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function MonthlyBar({ series }) {
  const max = Math.max(1, ...series.map((s) => Math.max(s.paid, s.refunded, s.pending)));
  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6" data-testid="pa-monthly-trend">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-bold text-lg flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-[#FF5A00]" /> Monthly trend
        </div>
        <div className="text-[11px] uppercase font-mono-accent tracking-widest text-zinc-500">
          Paid · Refunded · Pending
        </div>
      </div>
      {series.length === 0 ? (
        <div className="text-sm text-zinc-400 text-center py-6">No data.</div>
      ) : (
        <div className="flex items-end gap-2 overflow-x-auto py-4 min-h-[180px]">
          {series.map((m) => (
            <div key={m.month} className="flex flex-col items-center min-w-[54px]" data-testid={`pa-month-${m.month}`}>
              <div className="flex items-end gap-0.5 h-32">
                <div
                  title={`Paid ${money(m.paid)}`}
                  className="w-3 rounded-t bg-emerald-500"
                  style={{ height: `${(m.paid / max) * 100}%` }}
                />
                <div
                  title={`Refunded ${money(m.refunded)}`}
                  className="w-3 rounded-t bg-rose-400"
                  style={{ height: `${(m.refunded / max) * 100}%` }}
                />
                <div
                  title={`Pending ${money(m.pending)}`}
                  className="w-3 rounded-t bg-amber-400"
                  style={{ height: `${(m.pending / max) * 100}%` }}
                />
              </div>
              <div className="text-[10px] text-zinc-500 mt-2 font-mono-accent">{m.month.slice(2)}</div>
              <div className="text-[10px] text-emerald-700 font-mono-accent">{money(m.paid)}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TechnicianTable({ rows }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp p-6" data-testid="pa-technicians">
      <div className="flex items-center justify-between mb-4">
        <div className="font-heading font-bold text-lg flex items-center gap-2">
          <Users2 className="w-4 h-4 text-[#FF5A00]" /> Technician attribution
        </div>
        <div className="text-[11px] uppercase font-mono-accent tracking-widest text-zinc-500">
          Verified links only
        </div>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-zinc-400 text-center py-6">No paid invoices in range.</div>
      ) : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="text-[10px] uppercase font-mono-accent text-zinc-500 tracking-widest">
            <tr>
              <th className="text-left py-2">Technician</th>
              <th className="text-right py-2">Gross</th>
              <th className="text-right py-2">Refunded</th>
              <th className="text-right py-2">Net</th>
              <th className="text-right py-2"># Payments</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.technician} className="border-b border-zinc-100 last:border-0" data-testid={`pa-tech-${r.technician}`}>
                <td className="py-2 font-medium">
                  {r.technician === "unassigned" ? (
                    <span className="text-zinc-500 italic">Unassigned</span>
                  ) : r.technician}
                </td>
                <td className="py-2 text-right font-mono-accent">{money(r.gross)}</td>
                <td className="py-2 text-right font-mono-accent text-rose-600">{money(r.refunded)}</td>
                <td className="py-2 text-right font-mono-accent font-semibold text-emerald-700">{money(r.net)}</td>
                <td className="py-2 text-right font-mono-accent">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table></div>
      )}
      <div className="mt-3 text-[11px] text-zinc-500">
        Only counts payments where invoice → job → assigned technician is populated. Unlinked payments are grouped under Unassigned.
      </div>
    </div>
  );
}

export default function PaymentsAnalyticsPanel() {
  const today = new Date().toISOString().slice(0, 10);
  const [start, setStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [end, setEnd] = useState(today);
  const [summary, setSummary] = useState(null);
  const [monthly, setMonthly] = useState([]);
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { from: toISO(start), to: toISO(end, true) };
      const [s, m, t] = await Promise.all([
        api.get("/payments/analytics/summary", { params }),
        api.get("/payments/analytics/monthly", { params: { months: 12 } }),
        api.get("/payments/analytics/by-technician", { params }),
      ]);
      setSummary(s.data);
      setMonthly(m.data.months || []);
      setTechs(t.data.technicians || []);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error("Payments analytics is owner-only.");
      } else {
        toast.error(e.response?.data?.detail || "Failed to load payments analytics");
      }
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => summary || null, [summary]);

  return (
    <section className="mt-8" data-testid="payments-analytics-panel">
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs font-mono-accent uppercase tracking-widest text-[#FF5A00] mb-1">Payments</div>
          <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-[#FF5A00]" /> Analytics
          </h2>
        </div>
        <div className="flex items-end gap-2 flex-wrap" data-testid="pa-filter-bar">
          <div>
            <div className="text-[10px] uppercase font-mono-accent text-zinc-500 mb-1">From</div>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-40" data-testid="pa-start-input" />
          </div>
          <div>
            <div className="text-[10px] uppercase font-mono-accent text-zinc-500 mb-1">To</div>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-40" data-testid="pa-end-input" />
          </div>
          <Button onClick={load} variant="outline" size="sm" data-testid="pa-reload-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}
            Reload
          </Button>
        </div>
      </div>

      {loading && !kpis ? (
        <div className="p-12 text-center text-zinc-500"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : !kpis ? (
        <div className="p-12 text-center text-zinc-400">No data.</div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
            <Kpi icon={DollarSign} label="Gross"      value={money(kpis.gross)}            tone="text-emerald-700" testid="pa-kpi-gross"/>
            <Kpi icon={Wallet}     label="Net"        value={money(kpis.net)}              tone="text-emerald-700" sub="Paid − Refunded" testid="pa-kpi-net"/>
            <Kpi icon={TrendingUp} label="Paid"       value={money(kpis.paid.amount)}      sub={`${kpis.paid.count} payments`} testid="pa-kpi-paid"/>
            <Kpi icon={Clock}      label="Pending"    value={money(kpis.pending.amount)}   sub={`${kpis.pending.count} initiated`} testid="pa-kpi-pending"/>
            <Kpi icon={RotateCcw}  label="Refunded"   value={money(kpis.refunded.amount)}  tone="text-rose-600" testid="pa-kpi-refunded"/>
            <Kpi icon={ShieldAlert}label="Disputed"   value={money(kpis.disputed.amount)}  tone="text-rose-600" sub={`${kpis.disputed.count} disputes`} testid="pa-kpi-disputed"/>
            <Kpi icon={XCircle}    label="Failed"     value={money(kpis.failed.amount)}    tone="text-rose-600" sub={`${kpis.failed.count} attempts`} testid="pa-kpi-failed"/>
            <Kpi icon={AlertTriangle} label="Partial" value={money(kpis.partial.amount)}   sub={`${kpis.partial.count} invoices`} testid="pa-kpi-partial"/>
            <Kpi icon={AlertTriangle} label="Outstanding" value={money(kpis.outstanding.amount)} sub={`${kpis.outstanding.count} unpaid`} tone="text-amber-600" testid="pa-kpi-outstanding"/>
          </div>

          <MonthlyBar series={monthly} />

          <div className="mt-4">
            <TechnicianTable rows={techs} />
          </div>

          <div className="mt-3 text-[11px] text-zinc-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" /> Range: {kpis.range.from?.slice(0, 10)} → {kpis.range.to?.slice(0, 10)} · Uses stored payment + webhook data (no live Stripe calls).
          </div>
        </>
      )}
    </section>
  );
}
